/**
 * Display preparation for the respiration chart.
 *
 * The analysis service computes respiration in 60-second windows, because that
 * is what buys ~1 breath/min frequency resolution. That is a computation
 * constraint, not a display one — and plotting it raw would be misleading:
 * per-window error is 0.5-1.2 br/min while genuine breathing variation is
 * ~1.2 br/min, so at 60-second resolution roughly half the visible jitter is
 * measurement noise. Over 24 hours that is ~1440 points of it.
 *
 * Aggregating five windows into one 5-minute point cuts random error by about
 * sqrt(5) while the real variation — which moves far more slowly than a minute
 * — survives. The stored series stays at 60 s so later work (sleep-apnoea
 * screening) keeps full resolution.
 */
import { RespirationTimeSeriesData, RespirationChartChunk } from '../types/respirationTimeSeries';

/** Windows below this confidence are not measurements and are discarded. */
export const RESPIRATION_MIN_CONFIDENCE = 0.3;

/** Number of 60-second windows combined into one displayed point. */
export const RESPIRATION_WINDOWS_PER_POINT = 5;

/**
 * A displayed point needs at least this many resolved windows, so that one
 * lucky window in five is never drawn as a solid reading.
 */
export const RESPIRATION_MIN_WINDOWS_PER_POINT = 2;

/**
 * Upper bound of the validated range, in breaths per minute.
 *
 * Above roughly 21 br/min the estimator underestimates by ~7 br/min, and the
 * confidence score does not catch it because all three extraction methods fail
 * in the same direction and therefore still agree. Points above this bound are
 * withheld rather than drawn, since a clinician has no way to tell an accurate
 * 19 from a badly underestimated 26.
 */
export const RESPIRATION_MAX_DISPLAY_BRPM = 18;

const SIX_HOURS_IN_SECONDS = 6 * 60 * 60;

/** Median of a non-empty list. Median, not mean, so one bad window cannot drag a point. */
function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/**
 * Collapse 60-second windows into 5-minute display points.
 *
 * Windows below the confidence floor are dropped before aggregating, so they
 * cannot pull a median. A point whose value lands outside the validated range
 * is returned as null.
 */
export function aggregateForDisplay(
  data: RespirationTimeSeriesData,
  windowsPerPoint: number = RESPIRATION_WINDOWS_PER_POINT,
): { timestamps: number[]; respirationRate: (number | null)[] } {
  const timestamps: number[] = [];
  const respirationRate: (number | null)[] = [];

  if (!data?.timestamps?.length) {
    return { timestamps, respirationRate };
  }

  const rates = data.respirationRate ?? [];
  const confidences = data.confidence ?? [];

  for (let i = 0; i < data.timestamps.length; i += windowsPerPoint) {
    const usable: number[] = [];

    for (let j = i; j < Math.min(i + windowsPerPoint, data.timestamps.length); j += 1) {
      const rate = rates[j];
      const confidence = confidences[j];

      if (
        typeof rate === 'number'
        && Number.isFinite(rate)
        && typeof confidence === 'number'
        && confidence >= RESPIRATION_MIN_CONFIDENCE
      ) {
        usable.push(rate);
      }
    }

    timestamps.push(data.timestamps[i]);

    if (usable.length < RESPIRATION_MIN_WINDOWS_PER_POINT) {
      respirationRate.push(null);
    } else {
      const value = median(usable);

      respirationRate.push(value > RESPIRATION_MAX_DISPLAY_BRPM ? null : Number(value.toFixed(1)));
    }
  }

  return { timestamps, respirationRate };
}

/**
 * Split aggregated data into 6-hour chunks, matching the HRV chart so a long
 * recording renders as several readable panels rather than one dense one.
 */
export function chunkRespirationInSixHours(
  aggregated: { timestamps: number[]; respirationRate: (number | null)[] },
): RespirationChartChunk[] {
  const chunks: RespirationChartChunk[] = [];

  if (!aggregated.timestamps.length) {
    return chunks;
  }

  let chunkStart = aggregated.timestamps[0];
  let current: RespirationChartChunk = { timestamps: [], respirationRate: [] };

  for (let i = 0; i < aggregated.timestamps.length; i += 1) {
    if (aggregated.timestamps[i] - chunkStart >= SIX_HOURS_IN_SECONDS) {
      chunks.push(current);
      chunkStart = aggregated.timestamps[i];
      current = { timestamps: [], respirationRate: [] };
    }

    current.timestamps.push(aggregated.timestamps[i]);
    current.respirationRate.push(aggregated.respirationRate[i]);
  }

  if (current.timestamps.length > 0) {
    chunks.push(current);
  }

  return chunks;
}

/** Whether any point survived aggregation and is therefore worth drawing. */
export function hasDisplayableData(
  aggregated: { respirationRate: (number | null)[] },
): boolean {
  return aggregated.respirationRate.some((v) => v !== null);
}
